import { useEffect, useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Bot, Check, ChevronDown, ChevronRight, Hash, Lock, LogOut, Megaphone, Moon, Plus, Search, Settings, Sun } from "lucide-react";
import type { ThemePreference } from "../theme";
import { Avatar } from "../ui/Avatar";
import { cn } from "../ui/cn";
import { conversationLabel, isAdmin, type Conversation, type MeReady } from "./types";

type Props = {
  me: MeReady;
  conversations: readonly Conversation[];
  selectedId: string | null;
  open: boolean;
  onSelect: (id: string) => void;
  onSearch: () => void;
  onCreateChannel: () => void;
  onBrowseChannels: () => void;
  onNewDirectMessage: () => void;
  onOpenAdmin: () => void;
  workspaces: MeReady["workspaces"];
  switchingWorkspaceId: string | null;
  onSwitchWorkspace: (workspaceId: string) => void;
  theme: ThemePreference;
  onCycleTheme: () => void;
};

export function Sidebar({ me, conversations, selectedId, open, onSelect, onSearch, onCreateChannel, onBrowseChannels, onNewDirectMessage, onOpenAdmin, workspaces, switchingWorkspaceId, onSwitchWorkspace, theme, onCycleTheme }: Props) {
  const { signOut } = useAuthActions();
  const [channelsExpanded, setChannelsExpanded] = useState(true);
  const [dmsExpanded, setDmsExpanded] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const channels = conversations.filter((conversation) => conversation.type !== "direct" && conversation.type !== "group_direct").sort((left, right) => (left.name ?? "").localeCompare(right.name ?? ""));
  const directs = conversations.filter((conversation) => conversation.type === "direct").sort((left, right) => (right.lastMessageAt ?? 0) - (left.lastMessageAt ?? 0));
  const metaKeyLabel = /Mac|iPhone|iPad/u.test(navigator.userAgent) ? "⌘" : "Ctrl";
  const ThemeIcon = theme === "dark" ? Sun : Moon;
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); setMenuOpen(false); } };
    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); };
  }, [menuOpen]);

  return (
    <aside className={cn("sidebar", open && "sidebar--open")} aria-label="Conversations">
      <div className="sidebar__header">
        <button type="button" onClick={() => { setMenuOpen((current) => !current); }} aria-expanded={menuOpen} aria-haspopup="menu">
          <span className="workspace-name">{me.workspace.name}</span>
          <ChevronDown size={14} />
        </button>
        {menuOpen ? (
          <div className="popover-menu" role="menu">
            <div className="popover-menu__who"><Avatar name={me.displayName} small photoUrl={me.avatarUrl} /><div><strong>{me.displayName}</strong><span>{me.user.email}</span></div></div>
            {workspaces.length > 1 ? (
              <div className="workspace-menu" aria-label="Your workspaces">
                <span className="workspace-menu__label">Switch workspace</span>
                {workspaces.map((workspace) => {
                  const selectedWorkspace = workspace.id === me.workspace.id;
                  return (
                    <button
                      key={workspace.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={selectedWorkspace}
                      disabled={switchingWorkspaceId !== null}
                      onClick={() => { setMenuOpen(false); onSwitchWorkspace(workspace.id); }}
                    >
                      <span className="workspace-menu__badge">{workspace.name.slice(0, 1).toUpperCase()}</span>
                      <span>{workspace.name}</span>
                      {selectedWorkspace ? <Check className="workspace-menu__check" size={14} /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {isAdmin(me.role) ? <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onOpenAdmin(); }}><Settings size={14} /> Members, invitations & agents</button> : null}
            <button type="button" role="menuitem" aria-label={`Theme: ${theme}. Change theme.`} onClick={() => { setMenuOpen(false); onCycleTheme(); }}><ThemeIcon size={14} /> Use {theme === "dark" ? "light" : "dark"} theme</button>
            <button type="button" role="menuitem" onClick={() => { void signOut(); }}><LogOut size={14} /> Sign out</button>
          </div>
        ) : null}
      </div>
      <button className="quick-search" type="button" onClick={onSearch}><Search size={14} /><span>Search</span><kbd>{metaKeyLabel} K</kbd></button>

      <div className="nav-section">
        <div className="nav-section__title">
          <button type="button" onClick={() => { setChannelsExpanded((current) => !current); }} aria-expanded={channelsExpanded}>
            {channelsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Channels
          </button>
          <div className="nav-section__actions">
            <button type="button" className="icon-mini" aria-label="Browse channels" title="Browse channels" onClick={onBrowseChannels}><Search size={12} /></button>
            <button type="button" className="icon-mini" aria-label="Create channel" title="Create channel" onClick={onCreateChannel}><Plus size={13} /></button>
          </div>
        </div>
        {channelsExpanded ? channels.map((conversation) => (
          <button key={conversation.id} type="button" className={cn("nav-row", conversation.id === selectedId && "nav-row--selected", conversation.unreadCount > 0 && "nav-row--unread")} onClick={() => { onSelect(conversation.id); }} aria-current={conversation.id === selectedId ? "page" : undefined}>
            {conversation.type === "private_channel" ? <Lock size={14} /> : conversation.type === "announcement" ? <Megaphone size={14} /> : <Hash size={14} />}
            <span>{conversation.name}</span>
            {conversation.unreadCount > 0 ? <strong aria-label={`${conversation.unreadCount} unread`}>{conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}</strong> : null}
          </button>
        )) : null}
      </div>

      <div className="nav-section">
        <div className="nav-section__title">
          <button type="button" onClick={() => { setDmsExpanded((current) => !current); }} aria-expanded={dmsExpanded}>
            {dmsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Direct messages
          </button>
          <div className="nav-section__actions">
            <button type="button" className="icon-mini" aria-label="New direct message" title="New direct message" onClick={onNewDirectMessage}><Plus size={13} /></button>
          </div>
        </div>
        {dmsExpanded ? directs.map((conversation) => (
          <button key={conversation.id} type="button" className={cn("nav-row nav-row--person", conversation.id === selectedId && "nav-row--selected", conversation.unreadCount > 0 && "nav-row--unread")} onClick={() => { onSelect(conversation.id); }} aria-current={conversation.id === selectedId ? "page" : undefined}>
            <Avatar name={conversationLabel(conversation)} kind={conversation.partner?.kind} small />
            <span>{conversationLabel(conversation)}</span>
            {conversation.partner?.kind === "openclaw" ? <Bot size={13} aria-label="AI teammate" /> : null}
            {conversation.unreadCount > 0 ? <strong aria-label={`${conversation.unreadCount} unread`}>{conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}</strong> : null}
          </button>
        )) : null}
        {dmsExpanded && directs.length === 0 ? <p className="nav-hint">No direct messages yet.</p> : null}
      </div>

      <div className="sidebar__footer">
        <button type="button" className="footer-user" aria-expanded={menuOpen} aria-haspopup="menu" onClick={() => { setMenuOpen((current) => !current); }}>
          <Avatar name={me.displayName} small photoUrl={me.avatarUrl} />
          <span>{me.displayName}</span>
        </button>
        <span className="security-indicator" title="Invite-only workspace">{me.role}</span>
      </div>
    </aside>
  );
}
